import { componentText, SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import { css, html, PropertyValues } from 'lit'
import { property } from 'lit/decorators.js'
import { createRef, type Ref, ref } from 'lit/directives/ref.js'
import { repeat } from 'lit/directives/repeat.js'
import {
	animationFrames,
	debounceTime,
	distinctUntilChanged,
	exhaustMap,
	filter,
	fromEvent,
	groupBy,
	map,
	merge,
	mergeMap,
	Subject,
	switchMap,
	takeUntil,
	takeWhile,
	tap,
} from 'rxjs'
import { fromResizeObserver } from '@schmancy/directives/layout'
import '@schmancy/primitives/typography/typography'
import { parseClock, timeSlots } from './time-picker.slots'
import {
	atRest,
	nearestDetent,
	project,
	rubberBanded,
	type Sample,
	seedVelocity,
	settleOmega,
	springAt,
	velocityFrom,
} from './time-picker.physics'

export type SchmancyTimePickerChangeEvent = CustomEvent<{ value: string }>

/** Where a wheel with no value parks: midday, so neither end of the day is
 *  implied by a picker nobody has answered yet. */
const RESTING = 12 * 60

/** How long a wheel stream must go quiet before the spin counts as released.
 *  A trackpad's own inertia tapers rather than stopping, so this fires after
 *  the OS has finished throwing and the measured velocity is whatever is
 *  genuinely left — which on a trackpad is nothing, and on a mouse wheel spun
 *  hard is a real flick this component then carries. */
const WHEEL_QUIET = 90

/** Readings kept per wheel. `velocityFrom` windows them itself; this only keeps
 *  a long drag from growing an unbounded array — 32 covers a quarter second at
 *  120Hz, comfortably more than the window ever asks for. */
const SAMPLE_CAP = 32

const pad = (n: number) => String(n).padStart(2, '0')

const instantly = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches

/** A grid of `step` multiples inside `0…59` — the minute wheel's own floor and
 *  the whole of the second wheel. */
const grid = (step: number): number[] => Array.from({ length: Math.ceil(60 / step) }, (_, i) => i * step)

const stepOf = (declared: number): number => Math.max(1, Math.floor(declared) || 1)

/** One cell on a wheel. `selectable` decides both the dim and — by being the
 *  only kind of cell that contributes a detent — whether the wheel can come to
 *  rest on it at all. */
interface Cell {
	value: number
	label: string
	selectable: boolean
}

type Lane = 'h' | 'm' | 's'

/**
 * One wheel's transport: the two nodes it writes, the layout it was last
 * measured against, where it is, and — while it is going somewhere — the spring
 * carrying it there.
 *
 * Every length here is MEASURED. The reference implementation this replaces
 * carried its row pitch as a literal pair of magic numbers beside a `gap` class,
 * so the physics desynced silently the moment the CSS moved; `cell`, `lead` and
 * `height` are read off the live boxes instead, and `depthBase` is derived from
 * all three rather than assuming the column is exactly three cells tall.
 */
interface Seat {
	readonly key: Lane
	readonly column: Ref<HTMLElement>
	readonly track: Ref<HTMLElement>
	/** Measured cell pitch, px. 0 until the wheel has layout — the one gate every
	 *  offset computation passes through. */
	cell: number
	/** The visible height of the wheel, and so the rubber band's dimension. */
	height: number
	/** Cells between the top of the track and the centre line, as a signed
	 *  number of cells — the whole of the spacer-and-centring geometry, folded
	 *  into one term so a frame costs one division. */
	depthBase: number
	/** The offsets of the last and first cells: the ends the band resists past. */
	lower: number
	upper: number
	/** Where the track is translated to right now. */
	offset: number
	/** Where it is going, or `null` when it is not going anywhere. */
	target: number | null
	from: number
	seed: number
	omega: number
	since: number
	samples: Sample[]
	dragging: boolean
}

/** One column. The wheel is ONE unit rendered per segment, never three
 *  near-identical blocks, so a fourth would be a fourth entry rather than a
 *  fourth copy. */
interface Segment {
	key: Lane
	name: string
	seat: Seat
	cells: Cell[]
	centre: number
}

interface Wheels {
	segments: Segment[]
	slots: number[]
	held: number | null
	second: number
}

/**
 * The latest slot at or before `wanted`, or the earliest slot of all when none
 * is — `null` only when the window admits nothing.
 *
 * At-or-before rather than nearest, because the consumer's `max` is minute-
 * granular while this grid is not: rounding 14:09 UP to 14:10 hands back a time
 * the caller then refuses, and on a panel seeded from `now` that refusal arrives
 * before the user has touched anything. Rounding down can only ever cross the
 * lower bound, and there the fallback carries it up to the first legal slot,
 * which is the one place going up is the only answer.
 */
function nearestSlot(slots: number[], wanted: number): number | null {
	if (!slots.length) return null
	const below = slots.filter(m => m <= wanted)
	return below.length ? below[below.length - 1] : slots[0]
}

/** The offset that puts cell `index` under the magnifier, and its inverse. Both
 *  derived from the measured geometry, so the two can never disagree. */
const offsetFor = (seat: Seat, index: number): number => (-index - seat.depthBase) * seat.cell
const indexAt = (seat: Seat, offset: number): number => -(seat.depthBase + offset / seat.cell)

/**
 * Picks a time of day on vertical wheels — hours and minutes side by side, three
 * values deep, the centred one selected, and seconds as a third column a
 * consumer asks for. Naive local wall clock, no date, no native
 * `<input type="time">` and so no OS wheel: the same idiom, keyboard and locale
 * on every device.
 *
 * **The centred value IS the selection.** There is no separate commit target: a
 * wheel coming to rest is the choice, and a tap on a neighbour glides it to the
 * centre. Out-of-window values still render — dimmed, so a column's geometry
 * never jumps as the bounds move — but they contribute no detent, so the wheel
 * cannot come to rest on one. That is the whole of the "never land on a dead
 * entry" rule, and it is enforced where the landing is CHOSEN rather than
 * corrected after the fact.
 *
 * **The transport is this component's own.** Each wheel is a transform under a
 * clipped column, driven by pointer, wheel and keyboard through one gesture
 * pipeline: a drag follows the finger one-to-one, a release is measured, the
 * resting place is projected analytically from that velocity and the friction,
 * the projection is snapped to the nearest SELECTABLE detent, and a critically
 * damped spring carries the wheel there without overshooting. Past the ends the
 * drag meets iOS's own rubber band. The arithmetic is all in
 * `time-picker.physics.ts`, where it can be executed on its own.
 *
 * A browser scroll container cannot express any of that: it exposes no velocity
 * to read, no deceleration to tune, no control of the settle and no give at the
 * ends, and its rest timing differs per engine. That is why the transport is
 * replaced rather than configured.
 *
 * **`change` is a gesture's word.** On open, and whenever the bounds move, each
 * wheel parks itself on the value it was HANDED — dimmed where the window or the
 * grid does not admit it, because a value outside the window is something its
 * owner has to be shown, not something this picker may quietly swap and then
 * report as a choice. What the wheels cannot show at all is the one correction
 * left: nothing handed, or a minute off a coarsened grid. That one IS reported,
 * because the alternative is a wheel showing a time its owner does not hold. So
 * a `null` value parks on the slot nearest midday and reports that; a value out
 * of window is shown, dead, and reported to nobody — the consumer holding it
 * already knows, and refusing it is its own to do. An inert picker reports
 * nothing, since every cell is dead and a wheel at rest is then a resting
 * position rather than an answer.
 *
 * `min`/`max` are a time of day ON the day already chosen — the consumer
 * resolves a datetime bound against its own drafted day before handing it here,
 * because a picker that has no date cannot know whether a bound applies to it.
 * They bound the minute; seconds are their own independent grid.
 *
 * @element schmancy-time-picker
 * @fires change - Committed time of day { value: 'HH:mm' }, or 'HH:mm:ss' under `seconds`
 */
@schmancyElement('schmancy-time-picker')
export class SchmancyTimePicker extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
			}

			/* CASCADE-CRITICAL, so these live here rather than on classes — each is
			   the transport itself rather than a treatment over it.

			   touch-action: none is what hands a vertical drag to this component
			   instead of letting the page scroll under it; without it a finger on a
			   phone pans the sheet and the wheel never sees the gesture.

			   overflow: clip, NOT hidden. Both clip, but hidden makes the column a
			   scroll container with zero range that the browser is still free to
			   scroll — moving focus to a cell is enough — and one such scroll
			   silently offsets every cell from the transform this component
			   believes it placed. clip creates no scroll container at all. */
			[role='listbox'] {
				touch-action: none;
				overflow: clip;
			}

			/* The curve. Each cell's distance from the centre line, in cells, drives
			   a scale and an opacity that peak dead centre, so the column reads as a
			   wheel falling away rather than a flat list.

			   It is arithmetic rather than an animation because there is no longer a
			   scroller to hang a view() timeline on: the offset belongs to this
			   component now, so the depth is derived from that same offset. ONE
			   custom property is written per wheel per frame, beside the transform
			   and on the same node; every cell resolves its own place from it and
			   its own index. Endpoints match what the keyframes this replaces
			   produced — full size and full ink at the centre, 0.66 and 0.3 one cell
			   out, linear between — so the wheel looks the same and costs one write
			   instead of a timeline per cell.

			   max(x, -1 * x) is abs() spelled in arithmetic every engine has had for
			   years. The dead-cell dim stays on the GLYPH inside the cell rather
			   than here, so the two opacities multiply. */
			[role='option'] {
				--depth: clamp(
					0,
					max(calc(var(--i) + var(--wheel-cells, 0)), calc(-1 * (var(--i) + var(--wheel-cells, 0)))),
					1
				);
				scale: calc(1 - 0.34 * var(--depth));
				opacity: calc(1 - 0.7 * var(--depth));
			}
		`,
	]

	/** `'HH:mm'` — or `'HH:mm:ss'` under {@link seconds} — or `null` for no time
	 *  chosen yet. */
	@property() value: string | null = null

	/** A time of day on the day already chosen. Absent means unbounded. */
	@property() min?: string
	@property() max?: string

	/** The minute grid. Most granular by default; a consumer coarsens it
	 *  deliberately. The window's own endpoints are admitted whatever it is. */
	@property({ type: Number, attribute: 'minute-step' }) minuteStep = 1

	/** Offer a third column and carry seconds in `value`. Off by default, so a
	 *  consumer holding `'HH:mm'` is untouched; on, `value` reads `'HH:mm:ss'`
	 *  and every consumer of it has to expect the longer shape. */
	@property({ type: Boolean, reflect: true }) seconds = false

	/** The second grid, on the same terms as {@link minuteStep}. */
	@property({ type: Number, attribute: 'second-step' }) secondStep = 1

	/** No time is a value without a day, so a panel with no day drafted yet hands
	 *  the picker down inert rather than letting it produce one. */
	@property({ type: Boolean, reflect: true }) disabled = false

	readonly #seats: readonly Seat[] = (['h', 'm', 's'] as const).map(key => ({
		key,
		column: createRef<HTMLElement>(),
		track: createRef<HTMLElement>(),
		cell: 0,
		height: 0,
		depthBase: 0,
		lower: 0,
		upper: 0,
		offset: 0,
		target: null,
		from: 0,
		seed: 0,
		omega: 0,
		since: 0,
		samples: [],
		dragging: false,
	}))

	/** Pinged whenever a wheel is given somewhere to go. The frame loop exists
	 *  only between that ping and the wheels arriving. */
	readonly #launched = new Subject<void>()

	override connectedCallback(): void {
		super.connectedCallback()

		// ONE pipeline, four legs.
		merge(
			// LAYOUT. Every height this element takes both invalidates the offsets a
			// park wrote and proves the columns exist to drive — the first one
			// arrives late, because mounted inside an overlay that had not laid out,
			// the first updated() measured zero and had nothing to divide by. The
			// park is idempotent, so re-running it costs a transform write to where
			// the wheels already are.
			fromResizeObserver(this).pipe(
				map(entries => entries[0]?.contentRect.height ?? 0),
				distinctUntilChanged(),
				filter(height => height > 0),
				tap(() => this.#parkOnValue()),
			),

			// THE DRAG. Pointer Events with capture, so mouse, touch and pen are one
			// gesture rather than three near-identical pipelines, and the session
			// ends on both events that can end one: pointerup, and the pointercancel
			// a touch the OS takes back fires instead of it. The spec guarantees one
			// or the other for every pointerdown, so the pair is the complete set —
			// and a wheel left mid-flight between two values is a wheel showing a
			// time nobody chose.
			//
			// They end it in DIFFERENT PLACES, though, and that is why they are wired
			// differently. A pointerup carries the position the finger left, so it is
			// the gesture's last reading and belongs in the stream that places the
			// wheel. A pointercancel carries no position at all — the spec leaves its
			// coordinates meaningless and Chromium reports 0 — so it can only be a
			// terminator. Measured here: placing it read as a 678px upward drag and
			// threw the wheel to the far end of the column.
			//
			// exhaustMap, not switchMap: a second press while a gesture is in flight
			// is not a new gesture to switch to, and switching to it would abandon
			// the first seat mid-drag with nothing left to settle or release it.
			fromEvent<PointerEvent>(this, 'pointerdown').pipe(
				filter(down => down.isPrimary && !this.disabled),
				map(down => ({ down, seat: this.#seatUnder(down) })),
				filter((grab): grab is { down: PointerEvent; seat: Seat } => grab.seat !== null),
				exhaustMap(({ down, seat }) => {
					down.preventDefault()
					this.setPointerCapture(down.pointerId)
					seat.column.value?.focus({ preventScroll: true })
					this.#measure()
					seat.target = null
					seat.dragging = true
					seat.samples = [{ at: down.timeStamp / 1000, y: seat.offset }]
					const held = seat.offset
					// Two streams over one gesture, because a release is a POSITION but
					// not a SPEED.
					//
					// Every move is a velocity reading. The release is not: the finger is
					// stationary for the frame before it lifts on every gesture ever
					// made, so counting that frame as deceleration reads a throw as a
					// crawl — measured here, a flick that carried 27 values carried 5
					// once the release joined the fit. It is still a placement, though,
					// and takeWhile is inclusive so the wheel ends where the finger did:
					// a browser is free to coalesce pointermove, and a gesture whose last
					// delivered move sits behind its release would otherwise land short.
					//
					// (This completes the gesture stream, which is what a gesture ending
					// means. It is not the reference implementation's
					// `takeWhile(() => !this.dragging)`, which completed a STORE
					// subscription and left the component deaf from the first drag on.)
					return merge(
						fromEvent<PointerEvent>(this, 'pointermove').pipe(
							filter(move => move.pointerId === down.pointerId),
							// The RESISTED offset, not the finger's: a hand still pulling
							// hard against the end is barely moving the wheel, and the
							// momentum it is released with is the wheel's.
							tap(move => {
								const raw = held + (move.clientY - down.clientY)
								seat.samples.push({
									at: move.timeStamp / 1000,
									y: rubberBanded(raw, seat.lower, seat.upper, seat.height),
								})
								if (seat.samples.length > SAMPLE_CAP) seat.samples.shift()
							}),
						),
						fromEvent<PointerEvent>(this, 'pointerup').pipe(filter(up => up.pointerId === down.pointerId)),
					).pipe(
						takeWhile(at => at.type === 'pointermove', true),
						tap(at => {
							const raw = held + (at.clientY - down.clientY)
							this.#place(seat, rubberBanded(raw, seat.lower, seat.upper, seat.height))
						}),
						takeUntil(
							fromEvent<PointerEvent>(this, 'pointercancel').pipe(filter(lost => lost.pointerId === down.pointerId)),
						),
						tap({
							complete: () => {
								seat.dragging = false
								this.#release(seat)
							},
						}),
					)
				}),
			),

			// THE WHEEL. Giving up native scroll gives up the trackpad with it, so it
			// comes back here — on the same offset, the same band and the same
			// release, with no device sniffing anywhere: a line is one cell, a page
			// one column, a pixel a pixel. What separates a trackpad from a mouse is
			// then automatic. macOS has already spent the flick's inertia by the time
			// the stream goes quiet, so the measured velocity is nil and the wheel
			// simply snaps; a mouse wheel spun hard is still moving, and the same
			// projection carries it on.
			fromEvent<WheelEvent>(this, 'wheel', { passive: false }).pipe(
				filter(() => !this.disabled),
				map(spin => ({ spin, seat: this.#seatUnder(spin) })),
				filter((turn): turn is { spin: WheelEvent; seat: Seat } => turn.seat !== null),
				tap(({ spin, seat }) => {
					spin.preventDefault()
					if (!seat.cell) this.#measure()
					if (!seat.cell) return
					seat.target = null
					const unit = spin.deltaMode === 1 ? seat.cell : spin.deltaMode === 2 ? seat.height : 1
					this.#place(seat, rubberBanded(seat.offset - spin.deltaY * unit, seat.lower, seat.upper, seat.height))
					seat.samples.push({ at: spin.timeStamp / 1000, y: seat.offset })
					if (seat.samples.length > SAMPLE_CAP) seat.samples.shift()
				}),
				// Per column: two wheels spun in turn each get their own release,
				// where one shared debounce would settle the second and leave the
				// first stopped between two values. Bounded by the columns rendered,
				// which is two or three.
				groupBy(({ seat }) => seat.key),
				mergeMap(column => column.pipe(debounceTime(WHEEL_QUIET))),
				tap(({ seat }) => this.#release(seat)),
			),

			// THE FRAME LOOP. It exists only while something is moving: a launch
			// starts it, and the first frame that finds every wheel arrived completes
			// the inner stream, which unsubscribes animationFrames and cancels the
			// request. At rest this component schedules nothing at all — no ticker,
			// no listener on a scroller, no timeline per cell.
			this.#launched.pipe(
				switchMap(() =>
					animationFrames().pipe(
						tap(({ timestamp }) => this.#advance(timestamp / 1000)),
						takeWhile(() => this.#seats.some(seat => seat.target !== null)),
						tap({ complete: () => this.#rested() }),
					),
				),
			),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe()
	}

	/** The wheel an event happened on, found by walking the composed path rather
	 *  than by which of three refs matched — so one listener on the host serves
	 *  however many columns are rendered, and a third column appearing needs no
	 *  re-established source. */
	#seatUnder(event: Event): Seat | null {
		for (const node of event.composedPath()) {
			if (node instanceof HTMLElement && node.dataset.wheel)
				return this.#seats.find(seat => seat.key === node.dataset.wheel) ?? null
		}
		return null
	}

	/** Places a wheel: the transform the eye follows, and the one number every
	 *  cell reads its own depth off. Both on the same node, both compositor-cheap,
	 *  and nothing else touched. */
	#place(seat: Seat, offset: number): void {
		seat.offset = offset
		const track = seat.track.value
		if (!track || !seat.cell) return
		track.style.transform = `translate3d(0, ${offset}px, 0)`
		track.style.setProperty('--wheel-cells', String(seat.depthBase + offset / seat.cell))
	}

	/** Advances every wheel in flight to where the spring says it is at `now`.
	 *  Closed form from the launch, not an integration, so a dropped frame costs
	 *  nothing and the trajectory is the same at 60Hz and 120Hz. */
	#advance(now: number): void {
		for (const seat of this.#seats) {
			if (seat.target === null) continue
			const { offset, velocity } = springAt(seat.from, seat.seed, now - seat.since, seat.omega)
			if (atRest(offset, velocity)) {
				this.#place(seat, seat.target)
				seat.target = null
				continue
			}
			this.#place(seat, seat.target + offset)
		}
	}

	/** Sends a wheel to `target`, arriving at the rate the gesture implies. Under
	 *  `prefers-reduced-motion` there is no journey at all — the wheel is simply
	 *  where it was going. */
	#settleTo(seat: Seat, target: number, velocity: number): void {
		if (instantly()) {
			seat.target = null
			this.#place(seat, target)
			this.#rested()
			return
		}
		seat.from = seat.offset - target
		seat.omega = settleOmega(seat.from, velocity)
		seat.seed = seedVelocity(seat.from, velocity, seat.omega)
		seat.since = performance.now() / 1000
		seat.target = target
		this.#launched.next()
	}

	/**
	 * The end of a gesture: measure what speed the wheel was left at, project
	 * where that speed alone would carry it, snap THAT to the nearest selectable
	 * detent, and settle onto it.
	 *
	 * Projecting first and snapping the projection is what makes a flick land
	 * where it was aimed. Decaying first and snapping wherever it stopped pulls
	 * the wheel off the value it just arrived at, which reads as the picker
	 * arguing with the hand that threw it.
	 */
	#release(seat: Seat): void {
		const segment = this.#measure().segments.find(s => s.seat === seat)
		if (!segment || !seat.cell) return
		const detents = segment.cells.flatMap((cell, index) => (cell.selectable ? [offsetFor(seat, index)] : []))
		const speed = instantly() ? 0 : velocityFrom(seat.samples, performance.now() / 1000)
		seat.samples = []
		const target = nearestDetent(detents, project(seat.offset, speed), Math.sign(speed))
		if (target === null) return
		this.#settleTo(seat, target, speed)
	}

	/** A tap on a neighbour and an arrow key: a move with nothing thrown behind
	 *  it, so it runs at the still rate rather than coasting. */
	#glide(seat: Seat, index: number): void {
		this.#measure()
		if (!seat.cell) return
		this.#settleTo(seat, offsetFor(seat, index), 0)
	}

	/** Says what the wheels are showing, once. Idempotent by value, so the park
	 *  and the settle can both call it and the second is a no-op. */
	#report(minutes: number, second: number): void {
		const spelled = this.#spell(minutes, second)
		if (this.value === spelled) return
		this.value = spelled
		this.dispatchEvent(
			new CustomEvent<SchmancyTimePickerChangeEvent['detail']>('change', {
				detail: { value: spelled },
				bubbles: true,
				composed: true,
			}),
		)
	}

	/**
	 * Every wheel has arrived. Read what they are centred on, correct a
	 * composition that lands between slots, and report it.
	 *
	 * The wheels move independently, so their composition can land between slots
	 * even though no column rested on a dead cell of its own — an hour the window
	 * opens part-way through is that case.
	 */
	#rested(): void {
		if (this.disabled) return
		const wheels = this.#measure()
		const resting = wheels.segments.map(s => {
			if (!s.seat.cell || !s.cells.length) return null
			const index = Math.round(indexAt(s.seat, s.seat.offset))
			return s.cells[Math.min(s.cells.length - 1, Math.max(0, index))]?.value ?? null
		})
		if (resting.some(value => value === null)) return
		const [hour, minute, second = 0] = resting as number[]
		const next = nearestSlot(wheels.slots, hour * 60 + minute)
		if (next === null) return
		if (next !== hour * 60 + minute) this.#parkOn(wheels, next, second)
		this.#report(next, second)
	}

	/**
	 * Parks on EVERY update, not only the ones whose changed-set names a bound.
	 * Lit can run an update before a property binding has been committed — a
	 * `.value` handed to a freshly created element in a nested template is the
	 * case — and a park gated on `changed.has('value')` then runs once against
	 * `null`, lands the wheels on the resting position, and has no later update to
	 * correct itself with. Measured live in `<schmancy-date-field-dialog>`: value
	 * `13:57`, min `13:09`, wheels parked on `13:09` — the slot nearest midday,
	 * which is what a null value asks for.
	 */
	protected override updated(changed: PropertyValues): void {
		super.updated(changed)
		this.#parkOnValue()
	}

	/**
	 * Says the corrected value BEFORE render rather than after. `#report` writes
	 * `value`, and a property written from `updated()` schedules a second update
	 * Lit warns about by name — so it happens here, where the wheels are then
	 * drawn for the time that will be reported instead of one they move off.
	 *
	 * It speaks only where the wheels could not show what they were handed:
	 * `#wheels` holds a value with a cell exactly as given, so `#report` spells
	 * what `value` already says and returns on its own idempotence.
	 */
	protected override willUpdate(changed: PropertyValues): void {
		super.willUpdate(changed)
		if (this.disabled) return
		const { held, second } = this.#wheels()
		if (held !== null) this.#report(held, second)
	}

	/** `'HH:mm'`, or `'HH:mm:ss'` while the second column is on. */
	#spell(minutes: number, second: number): string {
		const clock = `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`
		return this.seconds ? `${clock}:${pad(second)}` : clock
	}

	/**
	 * Parks every wheel on the held value — the one handed in wherever a cell
	 * exists for it, the nearest selectable one where none does. `willUpdate` has
	 * already reported that second case, so this only ever moves the wheels onto
	 * a value the outside world either gave or has been told about.
	 *
	 * A wheel the user is holding, or one still flying, is left alone: the hand
	 * wins, and what it lands on is reported and becomes the value. The guard is
	 * read on every call and terminates nothing — the reference implementation
	 * spelled the same intent as `takeWhile(() => !this.dragging)`, which
	 * COMPLETES the store subscription the first time it is true and leaves the
	 * component deaf to every later external change.
	 */
	#parkOnValue(): void {
		if (this.#seats.some(seat => seat.dragging || seat.target !== null)) return
		const wheels = this.#measure()
		if (wheels.held !== null) this.#parkOn(wheels, wheels.held, wheels.second)
	}

	/**
	 * Centres every wheel on `minutes` (+ `second`), at once. A column with no
	 * layout to divide by leaves ALL of them alone, so they never half-move.
	 *
	 * Always instant. This park is a resting position rather than a movement the
	 * user began — the drag they begin is a real gesture, and the tap and arrow
	 * paths glide themselves.
	 */
	#parkOn(wheels: Wheels, minutes: number, second: number): void {
		const wanted = [Math.floor(minutes / 60), minutes % 60, second]
		const moves = wheels.segments.map((s, i) => ({
			seat: s.seat,
			index: s.cells.findIndex(c => c.value === wanted[i]),
		}))
		if (moves.some(move => !move.seat.cell || move.index < 0)) return
		for (const move of moves) {
			move.seat.target = null
			this.#place(move.seat, offsetFor(move.seat, move.index))
		}
	}

	/**
	 * The wheels, with every seat's geometry re-read from the live boxes: the
	 * cell's own pitch, the lead spacer's own height and the column's own height,
	 * so nothing here restates a number the stylesheet already owns and a cell
	 * that changes size takes the physics with it.
	 *
	 * `offsetHeight`, not `getBoundingClientRect()`: the cells carry a `scale`,
	 * and a scaled rect would report the magnifier's own distortion as the pitch.
	 */
	#measure(): Wheels {
		const wheels = this.#wheels()
		for (const segment of wheels.segments) {
			const seat = segment.seat
			const column = seat.column.value
			const lead = seat.track.value?.firstElementChild
			const cell = seat.track.value?.children[1]
			if (
				!column ||
				!(lead instanceof HTMLElement) ||
				!(cell instanceof HTMLElement) ||
				!column.clientHeight ||
				!cell.offsetHeight
			) {
				seat.cell = 0
				continue
			}
			seat.cell = cell.offsetHeight
			seat.height = column.clientHeight
			seat.depthBase = (lead.offsetHeight + cell.offsetHeight / 2 - column.clientHeight / 2) / cell.offsetHeight
			seat.upper = offsetFor(seat, 0)
			seat.lower = offsetFor(seat, segment.cells.length - 1)
		}
		return wheels
	}

	/**
	 * Every wheel's cells, and the slot set behind them — one derivation, so a
	 * column's index means the same thing to `render`, to the settle and to the
	 * park. The minute wheel carries the whole grid plus any window endpoint
	 * landing inside the drafted hour: rendering the grid and dimming what falls
	 * outside keeps the wheel's rhythm fixed as the bounds move, and the endpoint
	 * is what keeps a narrow window from being a column of nothing but dead cells.
	 *
	 * `held` is the one number the rest of this derivation hangs off — the
	 * position the wheels are parked on — and both the hour and the minute cells
	 * are read from it. Derived from anything else, the minute column carries the
	 * cells of one hour while the park aims at another, so the park indexes a list
	 * the DOM does not have. Measured live: value 13:59 against a 13:09 floor
	 * parked the hour on 14 and the minute on 09, because 14:00 is a slot and
	 * 13:00 is not.
	 */
	#wheels(): Wheels {
		const minuteStep = stepOf(this.minuteStep)
		const secondStep = stepOf(this.secondStep)
		const slots = timeSlots(minuteStep, this.min, this.max)
		const parsed = parseClock(this.value)
		// A minute this wheel HAS a cell for is held exactly as it was handed over,
		// selectable or not. The grid renders every multiple of the step and the
		// window's own endpoints join it, so a value seeded outside [min, max] sits
		// under the magnifier dimmed — which is what it is — rather than being
		// swapped for the nearest legal one and reported as though a hand had put
		// it there. The only minute corrected is one with no cell to sit on:
		// nothing handed at all, or one off a coarsened grid. Those are exactly
		// what `willUpdate` reports, because they are the only times this picker
		// would otherwise show a time its owner does not hold.
		const held =
			parsed && (parsed.minutes % minuteStep === 0 || slots.includes(parsed.minutes))
				? parsed.minutes
				: nearestSlot(slots, parsed?.minutes ?? RESTING)
		const hour = held === null ? null : Math.floor(held / 60)
		const secondCells = grid(secondStep)
		const second = nearestSlot(secondCells, parsed?.seconds ?? 0) ?? 0
		const minuteCells = [
			...new Set([
				...grid(minuteStep),
				...slots.filter(m => hour !== null && Math.floor(m / 60) === hour).map(m => m % 60),
			]),
		].sort((a, b) => a - b)

		const segments: Segment[] = [
			{
				key: 'h',
				name: componentText('timePicker.hours'),
				seat: this.#seats[0],
				centre: hour ?? 0,
				cells: Array.from({ length: 24 }, (_, h) => ({
					value: h,
					label: pad(h),
					selectable: !this.disabled && slots.some(m => Math.floor(m / 60) === h),
				})),
			},
			{
				key: 'm',
				name: componentText('timePicker.minutes'),
				seat: this.#seats[1],
				centre: held === null ? 0 : held % 60,
				cells: minuteCells.map(m => ({
					value: m,
					label: pad(m),
					selectable: !this.disabled && hour !== null && slots.includes(hour * 60 + m),
				})),
			},
		]
		if (this.seconds) {
			segments.push({
				key: 's',
				name: componentText('timePicker.seconds'),
				seat: this.#seats[2],
				centre: second,
				cells: secondCells.map(s => ({ value: s, label: pad(s), selectable: !this.disabled })),
			})
		}
		return { segments, slots, held, second }
	}

	/**
	 * THE DETENT PITCH is `h-10` (40px), and the column is three of them (`h-30`,
	 * 120px) with the magnifier on the middle third. One number decides all
	 * three, because `#measure` reads the cell's own `offsetHeight` and derives
	 * the column geometry from it — nothing here restates a length the
	 * stylesheet owns, so moving the pitch moves the physics with it.
	 *
	 * 40 rather than 44: a wheel's cell is not a button. The target a thumb aims
	 * at is the 120px column it drags, and the tap-a-neighbour path is the
	 * secondary one — so the cell keeps a 40px band the full width of its column,
	 * which is under the 44px floor a standalone control would owe and well over
	 * anything that reads as a cramped row. It bought the panel 12px, beside the
	 * 16 the enclosing `py-2` was spending on air.
	 */
	render() {
		const { segments } = this.#wheels()
		return html`
			<div class="relative flex items-stretch gap-2 h-30 select-none">
				<!-- ONE magnifier for the row, not one per column: the centred band is
				     where the time is read, and two bands would read as two answers. It
				     sits under the wheels and takes no pointer, so a drag still reaches
				     the column it looks like it lands on. -->
				<div
					class="pointer-events-none absolute inset-x-0 top-1/3 h-1/3 rounded-xl bg-surface-containerHighest"
					aria-hidden="true"
				></div>
				${repeat(
					segments,
					s => s.key,
					s => html`
						<div
							${ref(s.seat.column)}
							data-wheel=${s.key}
							role="listbox"
							tabindex=${this.disabled ? -1 : 0}
							aria-label=${s.name}
							aria-disabled=${this.disabled ? 'true' : 'false'}
							aria-activedescendant="${s.key}${s.centre}"
							class="relative h-30 flex-1 cursor-grab active:cursor-grabbing focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-primary-default"
							@keydown=${(e: KeyboardEvent) => {
								const step = e.key === 'ArrowDown' ? 1 : e.key === 'ArrowUp' ? -1 : 0
								if (!step || this.disabled) return
								e.preventDefault()
								const from = s.cells.findIndex(c => c.value === s.centre)
								for (let i = from + step; i >= 0 && i < s.cells.length; i += step) {
									if (!s.cells[i].selectable) continue
									this.#glide(s.seat, i)
									return
								}
							}}
						>
							<div ${ref(s.seat.track)}>
								<div class="h-10" aria-hidden="true"></div>
								${repeat(
									s.cells,
									c => c.value,
									(c, i) => html`
										<div
											id="${s.key}${c.value}"
											role="option"
											style="--i: ${i}"
											?data-selectable=${c.selectable}
											aria-selected=${c.value === s.centre ? 'true' : 'false'}
											aria-disabled=${c.selectable ? 'false' : 'true'}
											class=${this.classMap({
												'h-10 grid place-items-center tabular-nums': true,
												'cursor-pointer': c.selectable,
											})}
											@click=${() => {
												if (c.selectable) this.#glide(s.seat, i)
											}}
										>
											<schmancy-typography
												type="title"
												token="md"
												class=${this.classMap({
													'opacity-[var(--schmancy-sys-state-opacity-disabled,0.38)]': !c.selectable,
												})}
											>
												${c.label}
											</schmancy-typography>
										</div>
									`,
								)}
								<div class="h-10" aria-hidden="true"></div>
							</div>
						</div>
					`,
				)}
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-time-picker': SchmancyTimePicker
	}
}
